import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X, Translate } from "@phosphor-icons/react";
import { useImage } from "../lib/media";
import { useI18n } from "../lib/i18n";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const logo = useImage("logo", "https://customer-assets.emergentagent.com/job_yuva-modern/artifacts/d1s5jglr_Untitled_design__1_-removebg-preview.png");
  const { lang, setLang, t } = useI18n();

  const NAV = [
    { to: "/", key: "nav.home", testId: "home" },
    { to: "/about", key: "nav.about", testId: "about" },
    { to: "/treatments", key: "nav.treatments", testId: "treatments" },
    { to: "/blog", key: "nav.journal", testId: "journal" },
    { to: "/contact", key: "nav.contact", testId: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => setLang(lang === "en" ? "hi" : "en");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-xl border-b border-border/40" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={logo} alt="Yuva Ayurvedic Clinic Private Limited — specialist Ayurvedic men's sexual wellness clinic in Prayagraj, India" className="w-11 h-11 object-contain" loading="eager" decoding="async"/>
          <div className="leading-tight">
            <div className="font-serif-display text-base sm:text-lg text-forest leading-tight max-w-[180px] sm:max-w-none">Yuva Ayurvedic Clinic<br className="sm:hidden"/> <span className="sm:inline">Private Limited</span></div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-0.5">Prayagraj · Est. 2008</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.testId}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-terracotta" : "text-forest/80 hover:text-terracotta"}`
              }
              end={n.to === "/"}
            >
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={toggleLang}
            aria-label={t("lang.ariaLabel")}
            data-testid="lang-toggle"
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs tracking-wide text-forest hover:border-forest hover:bg-forest hover:text-cream transition-all"
          >
            <Translate size={14} weight="bold" />
            <span>{t("lang.toggle")}</span>
          </button>
          <Link
            to="/book-appointment"
            data-testid="header-book-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-2.5 text-sm tracking-wide hover:bg-terracotta transition-all duration-300 hover:-translate-y-0.5"
          >
            {t("cta.book")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-forest"
            onClick={() => setOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-border/40" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.testId}`}
                className={({ isActive }) =>
                  `text-lg font-serif-display ${isActive ? "text-terracotta" : "text-forest"}`
                }
              >
                {t(n.key)}
              </NavLink>
            ))}
            <Link
              to="/book-appointment"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-full bg-forest text-cream px-6 py-3"
              data-testid="mobile-book-btn"
            >
              {t("cta.book")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
